import React, { useEffect, useState, useRef } from "react";
import { useFormikContext, ErrorMessage } from "formik";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import Button from "../../../components/ui/Button";
import { dspDropdownOptions } from "../../Data/dropdownOptions";
import MultiSelect from "../MultiSelect/MultiSelect";
import ArtistTypeahead from "../../../components/roster/ArtistTypeahead";
import { useLocation } from "react-router-dom";
import { PitchFormValues } from "../Schema/pitchValidationSchema";
import { artistsApi } from "../../../repositories/artists";
import { soundchartsEndpoints } from "../../../repositories/soundcharts";
import Swal from "sweetalert2";
import { RequiredAsterisk } from "../../../components/common/RequiredAsterisk";
import InfoTooltip from "../../../components/common/InfoTooltip";
import CharacterCounterDisplay from "../../../components/common/CharacterCounterDisplay";
import UploadModal from "../../../components/modal/UploadModal";
import { ArrowUpTrayIcon, XMarkIcon, ChevronDownIcon, FolderIcon } from "@heroicons/react/24/outline";
import { ImageWithArtistId, Contributor, SelectedDSPs } from "../../types/form";
import ContributorList from "../contributors/ContributorList";
import { useModalManager } from "../../../hooks/useModalManager";
import ModalRenderer from "../../../components/modal/ModalRenderer";
import DspMarketingNotes from "../DspMarketingNotes/DspMarketingNotes";
import PlatformSpecificUpcs from "../PlatformSpecificUpcs/PlatformSpecificUpcs";
import { EmailContact, PersonalizedMessage } from "../../../types/email";
import { useAimsInsights } from "../../../hooks/useAimsInsights";
import { useUserPermissions } from "../../../hooks/useUserPermissions";
import * as Tooltip from "@radix-ui/react-tooltip";
import { computeArtistAutofill, cleanString, type AutofillField } from "./artistAutofill";
import { exclusiveMoods, subgenresAfterGenreChange } from "../../utils/optionRules";

interface LocationState {
  fromDashboard?: boolean;
}

interface EmailData {
  contactIds: number[];
  templateId: string;
  subject: string;
  templateContent?: string;
  selectedContacts?: EmailContact[];
  personalizedMessages?: PersonalizedMessage[];
}

interface GeneralReleaseInfoProps {
  setIsAddArtistModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  showAddArtistButton?: boolean;
  setArtistId?: (id: string) => void;
  selectedDSPs?: SelectedDSPs;
  emailData?: EmailData;
}
const GeneralReleaseInfo: React.FC<GeneralReleaseInfoProps> = ({
  setIsAddArtistModalOpen,
  showAddArtistButton = true,
  setArtistId,
  selectedDSPs = {
    spotify: false,
    amazon: false,
    deezer: false,
    audiomack: false,
    soundcloud: false,
    apple: false,
    TIDAL: false,
    youtube: false,
    email: false,
    siriusxm: false,
    vevo: false,
  },
  emailData,
}) => {
  // ...existing code...
  const { values, setFieldValue, errors, touched } = useFormikContext<PitchFormValues>();
  const location = useLocation();
  const state = location.state as LocationState;

  const [lastSelectedArtist, setLastSelectedArtist] = useState<string>("");
  // Fields written by the last artist autofill. Held in a ref (not state) because
  // handleArtistSelection reads it synchronously in the same tick it updates it.
  const autofilledFieldsRef = useRef<Set<AutofillField>>(new Set());
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredArtists, setFilteredArtists] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [artistIdforAssets, setArtistIdforAssets] = useState<string>("");
  const configOptions = ["Single", "Album", "EP", "Waterfall", "Mixtape", "Video", "Audiobook"];
  const [pitchImages, setPitchImages] = useState<ImageWithArtistId[]>([]);
  const [uploadedImages, setUploadedImages] = useState<{ url: string; imageTitle: string }[]>([]);
  const [selectedImages, setSelectedImages] = useState<{ url: string; imageTitle: string }[]>([]);
  const [modalDefaultTab, setModalDefaultTab] = useState<number>(0); // 0 = Upload, 1 = All Assets

  // AIMS AI Insights
  const { aims: aimsPermission } = useUserPermissions();
  // Show AI insights unless explicitly disabled (limit === 0)
  const hasAimsAccess = !!aimsPermission && aimsPermission.limit === null
  const { status: aimsStatus, result: aimsResult, error: aimsError, analyze: analyzeAudio, restore: restoreAims, reset: resetAims } = useAimsInsights();
  const audioInputRef = useRef<HTMLInputElement>(null);
  const [selectedAudioFile, setSelectedAudioFile] = useState<File | null>(null);
  const [selectedAimsArtists, setSelectedAimsArtists] = useState<string[]>([]);
  const [selectedAimsPlaylists, setSelectedAimsPlaylists] = useState<string[]>([]);
  // Guards the restore below so it runs once per pitch, not on every values change
  const restoredAimsIdRef = useRef<string | null>(null);

  // An existing pitch (edit / draft) may already carry an AIMS request id.
  // Re-poll it on mount so suggestions are available without re-uploading audio.
  useEffect(() => {
    const savedId = values.aimsRequestId;
    if (!hasAimsAccess || !savedId) return;
    if (restoredAimsIdRef.current === savedId) return;
    restoredAimsIdRef.current = savedId;
    restoreAims(savedId);
  }, [hasAimsAccess, values.aimsRequestId, restoreAims]);

  useEffect(() => {
    if (aimsResult) {
      setSelectedAimsArtists(aimsResult.suggestedArtists.slice(0, 3));
      setSelectedAimsPlaylists(aimsResult.suggestedPlaylists.slice(0, 3).map((p) => p.title));
    } else {
      setSelectedAimsArtists([]);
      setSelectedAimsPlaylists([]);
    }
  }, [aimsResult?.aimsRequestId]);

  // When polling completes and playlists arrive, default-select the first 3
  useEffect(() => {
    if (aimsResult && aimsResult.suggestedPlaylists.length > 0 && selectedAimsPlaylists.length === 0) {
      setSelectedAimsPlaylists(aimsResult.suggestedPlaylists.slice(0, 3).map((p) => p.title));
    }
  }, [aimsResult?.suggestedPlaylists.length]);

  // Artists also arrive with the poll response, so default-select them on arrival
  useEffect(() => {
    if (aimsResult && aimsResult.suggestedArtists.length > 0 && selectedAimsArtists.length === 0) {
      setSelectedAimsArtists(aimsResult.suggestedArtists.slice(0, 3));
    }
  }, [aimsResult?.suggestedArtists.length]);

  useEffect(() => {
    if (aimsStatus === "done") {
      Swal.fire({
        title: "Analysis Complete",
        text: "AI tag suggestions are ready. Review and apply them below.",
        icon: "success",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
      });
    } else if (aimsStatus === "partial") {
      Swal.fire({
        title: "Still Processing",
        text: "AI suggestions are taking longer than expected. Please try again in a few minutes.",
        icon: "info",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 6000,
        timerProgressBar: true,
      });
    } else if (aimsStatus === "error") {
      Swal.fire({
        title: "Analysis Failed",
        text: aimsError || "Try again in a few minutes, AIMS is not currently working",
        icon: "error",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
      });
    }
  }, [aimsStatus]);

  const handleAudioFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setSelectedAudioFile(file || null);
  };

  const handleAimsAnalyze = () => {
    if (!selectedAudioFile) return;
    const title = values.releaseTitle || "Untitled";
    analyzeAudio(selectedAudioFile, title);
  };

  const handleAimsReset = () => {
    resetAims();
    setSelectedAudioFile(null);
    if (audioInputRef.current) {
      audioInputRef.current.value = "";
    }
    // Reset only unloads the panel — it deliberately leaves values.aimsRequestId
    // alone so an existing pitch keeps its stored id until Apply Suggestions
    // overwrites it with a new analysis. The ref guard stays set to the saved id
    // so the restore effect does not immediately re-poll and undo this reset.
  };

  // Changing the main genre clears the subgenre picks it takes away; the
  // backend refuses a subgenre that isn't under the pitch's main genres.
  const changeMainGenres = (nextGenres: string[]) => {
    const updates = subgenresAfterGenreChange(values, values.mainGenre || [], nextGenres);
    setFieldValue("mainGenre", nextGenres);
    for (const [field, kept] of updates) setFieldValue(field, kept);
  };

  const applyAimsSuggestions = async () => {
    if (!aimsResult) return;

    const suggestedArtistNames = selectedAimsArtists.slice(0, 3);
    const suggestedPlaylistNames = selectedAimsPlaylists;

    const hasExistingGenre = (values.mainGenre?.length ?? 0) > 0;
    const hasExistingMood = (values.songMood?.length ?? 0) > 0;
    const hasExistingInstruments = (values.instruments?.length ?? 0) > 0;
    const hasExistingSimilarArtists = selectedDSPs?.amazon && !!values.similarArtists?.trim();
    const hasExistingSpotifyPlaylists = selectedDSPs?.spotify && (values.spotifyPlaylist?.length ?? 0) > 0;
    const hasPrefilledFields =
      hasExistingGenre || hasExistingMood || hasExistingInstruments ||
      hasExistingSimilarArtists || hasExistingSpotifyPlaylists;

    if (hasPrefilledFields) {
      const filledFields = [
        hasExistingGenre && "Genre",
        hasExistingMood && "Moods",
        hasExistingInstruments && "Instruments",
        hasExistingSimilarArtists && "Similar Artists",
        hasExistingSpotifyPlaylists && "Spotify Playlists",
      ]
        .filter(Boolean)
        .join(", ");

      const confirmation = await Swal.fire({
        title: "Override existing fields?",
        text: `AIMS will override your currently filled fields: ${filledFields}. Continue?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, override",
        cancelButtonText: "Keep existing",
      });

      if (!confirmation.isConfirmed) return;
    }

    if (aimsResult.mapped) {
      const { genres, moods, instruments } = aimsResult.mapped;
      if (genres.length > 0) changeMainGenres(genres.slice(0, 3));
      if (moods.length > 0) setFieldValue("songMood", moods.slice(0, 3));
      if (instruments.length > 0) setFieldValue("instruments", instruments);
    }

    if (selectedDSPs?.amazon && suggestedArtistNames.length > 0) {
      setFieldValue("similarArtists", suggestedArtistNames.join(" || "));
    }

    if (selectedDSPs?.spotify && suggestedPlaylistNames.length > 0) {
      setFieldValue("spotifyPlaylist", suggestedPlaylistNames);
    }

    // Store the AIMS request ID in form values so it is submitted with the pitch
    setFieldValue("aimsRequestId", aimsResult.aimsRequestId);
    Swal.fire({
      title: "Suggestions Applied",
      text: "AIMS suggestions have been applied to your form.",
      icon: "success",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });

  };

  // Contributor state
  const [contributorModalMode, setContributorModalMode] = useState<"add" | "edit">("add");
  const [selectedContributor, setSelectedContributor] = useState<Contributor | null>(null);

  // Modal manager for all modals
  const { openModal, closeModal, isOpen } = useModalManager();
  const [contributorToDelete, setContributorToDelete] = useState<Contributor | null>(null);
  // Auto-populate form fields when artist is selected from typeahead.
  // Uses computeArtistAutofill (pure fn) so behavior is unit-testable and
  // consistent with the "don't overwrite user input" invariant.
  const handleArtistSelection = async (artistName: string, artistData?: any) => {
    setFieldValue("artistName", artistName);

    // artistData is only passed on an actual dropdown selection — not on every
    // keystroke. So the lastSelectedArtist guard must only reset when the user
    // clearly abandons a selected artist (name cleared entirely).
    if (!artistData) {
      if (!artistName) {
        setLastSelectedArtist("");
      }
      return;
    }

    setArtistIdforAssets(artistData.id);
    if (setArtistId) setArtistId(artistData.id || "");

    if (artistName === lastSelectedArtist) return;
    setLastSelectedArtist(artistName);

    const patch = computeArtistAutofill(
      {
        spotifyUri: values.spotifyUri as string | undefined,
        asin: values.asin as string | undefined,
        appleId: values.appleId as string | undefined,
        artistHometown: values.artistHometown as string | undefined,
        artistCurrentTown: values.artistCurrentTown as string | undefined,
        appleCountry: values.appleCountry as string | undefined,
        distributor: values.distributor as string | undefined,
        TIDALpage: values.TIDALpage as string | undefined,
        soundCloudProfileUrl: values.soundCloudProfileUrl as string | undefined,
        soundCloudLabel: values.soundCloudLabel as string | undefined,
        language: values.language as string[] | undefined,
        socialsLinks: values.socialsLinks as string | undefined,
        similarArtists: values.similarArtists as string | undefined,
        listenersLocation: values.listenersLocation as string[] | undefined,
      },
      artistData,
      {
        countryDropdownOptions: dspDropdownOptions.artistCountry,
        languageDropdownOptions: dspDropdownOptions.language,
        listenersLocationOptions: dspDropdownOptions.listenersLocation,
      },
      autofilledFieldsRef.current,
    );

    for (const [field, value] of Object.entries(patch)) {
      setFieldValue(field, value);
    }

    // Remember which fields this artist owns so selecting a different artist can
    // replace them. Fields cleared to empty are no longer owned by anyone.
    autofilledFieldsRef.current = new Set(
      (Object.keys(patch) as AutofillField[]).filter((field) => {
        const value = patch[field];
        return value !== "" && !(Array.isArray(value) && value.length === 0);
      }),
    );

    // Surface the artist's roster photo as a promo image thumbnail, but only
    // when the user hasn't added/selected any images yet — never clobber their
    // own uploads or asset selections.
    if (artistData.photo && pitchImages.length === 0) {
      const artistPhoto: ImageWithArtistId = {
        url: cleanString(artistData.photo),
        imageTitle: `${artistName} Photo`,
        artistId: artistData.id,
      };
      setPitchImages([artistPhoto]);
      setFieldValue("images", [artistPhoto]);
    }

    Swal.fire({
      title: 'Artist Data Loaded',
      text: `Form has been populated with ${artistName}'s information from your roster.`,
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      toast: true,
      position: 'top-end'
    });
  };

  const mergeAndSetImages = (uploadArr: { url: string; imageTitle: string }[], selectArr: { url: string; imageTitle: string }[]) => {
    const merged = [...uploadArr, ...selectArr].filter(
      (item, idx, arr) => arr.findIndex(a => a.url === item.url) === idx
    );

    // Add artistId to each image object
    const mergedWithArtistId = merged.map(image => ({
      ...image,
      artistId: artistIdforAssets
    }));

    setPitchImages(mergedWithArtistId);
    setFieldValue("images", mergedWithArtistId);
  };

  const handleUploadSuccess = (newAssets: { url: string; imageTitle: string }[]) => {
    setUploadedImages(prev => {
      const accumulated = [...prev, ...newAssets].filter(
        (item, idx, arr) => arr.findIndex(a => a.url === item.url) === idx
      );
      mergeAndSetImages(accumulated, selectedImages);
      return accumulated;
    });
  };

  const syncSelectedAssets = (assets: { url: string; imageTitle: string }[]) => {
    setSelectedImages(assets);
    // Preserve uploaded images (current session + previously saved non-asset images)
    // by keeping any pitchImages not coming from the asset selection, then merging with new selection
    const selectedUrls = new Set(assets.map(a => a.url));
    const nonAssetImages = pitchImages.filter(img => !selectedUrls.has(img.url) && !selectedImages.some(s => s.url === img.url));
    const allImages = [...nonAssetImages, ...assets].filter(
      (item, idx, arr) => arr.findIndex(a => a.url === item.url) === idx
    );

    const mergedWithArtistId = allImages.map(image => ({
      ...image,
      artistId: (image as ImageWithArtistId).artistId ?? artistIdforAssets
    }));

    setPitchImages(mergedWithArtistId);
    setFieldValue("images", mergedWithArtistId);
  };

  const handleRemoveImage = (indexToRemove: number) => {
    const imageToRemove = pitchImages[indexToRemove];
    const updatedImages = pitchImages.filter((_, index) => index !== indexToRemove);

    // Update local states
    setPitchImages(updatedImages);
    setFieldValue("images", updatedImages);

    // Update selected images state (remove from both uploaded and selected)
    setUploadedImages(prev => prev.filter(img => img.url !== imageToRemove.url));
    setSelectedImages(prev => prev.filter(img => img.url !== imageToRemove.url));
  };


  // Check for new Spotify URI not in roster
  const checkSpotifyURI = async (spotifyUri: string) => {
    if (!spotifyUri || !spotifyUri.includes('spotify:artist:') && !spotifyUri.match(/^[0-9A-Za-z]{22}$/)) {
      return;
    }

    try {
      const artistId = spotifyUri.includes('spotify:artist:')
        ? spotifyUri.split('spotify:artist:')[1]
        : spotifyUri;

      const response = await artistsApi.checkSpotifyURI(artistId);

      if (!response.data) {
        const result = await Swal.fire({
          title: 'New Artist Detected',
          html: `
            <p>This Spotify URI is not in your roster.</p>
            <p><strong>Would you like to add this artist to your roster?</strong></p>
            <p class="text-sm text-neutral-600 mt-2">This will help auto-fill future pitches for this artist.</p>
          `,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Yes, Add to Roster',
          cancelButtonText: 'No, Continue',
        });

        if (result.isConfirmed) {

          // Prepare data for new artist
          const newArtistData = {
            artistName: values.artistName,
            spotifyURI: spotifyUri,
            asin: values.asin || 'TBD',
            appleID: values.appleId || '3343535',
            hometown: values.artistHometown || '',
            currentTown: values.artistCurrentTown || '',
            country: values.appleCountry || values.artistCountry || '',
            distributor: values.distributor || '',
            tidalURL: values.TIDALpage || 'https://tidal.com/browse/artist/tbd',
            soundcloudURL: values.soundCloudProfileUrl || 'https://soundcloud.com/tbd',
            // Parse social links if available
            instagram: '',
            tiktok: '',
            twitter: '',
            facebook: '',
            snapchat: ''
          };

          try {
            await artistsApi.create(newArtistData);

            Swal.fire({
              title: 'Artist Added!',
              text: `${values.artistName} has been added to your roster.`,
              icon: 'success',
              timer: 2000,
              showConfirmButton: false
            });
          } catch (error) {
            console.error('Error adding artist to roster:', error);
            Swal.fire({
              title: 'Error',
              text: 'Failed to add artist to roster. Please try again.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        }
      }
    } catch (error) {
      console.error('Error checking Spotify URI:', error);
      // Silently fail - don't interrupt user flow
    }
  };

  // Monitor Spotify URI changes
  useEffect(() => {
    if (values.spotifyUri && values.artistName) {
      const timeoutId = setTimeout(() => {
        checkSpotifyURI(values.spotifyUri as string);
      }, 1000); // Debounce for 1 second

      return () => clearTimeout(timeoutId);
    }
  }, [values.spotifyUri, values.artistName]);


  // Set artistId from existing images when form loads
  useEffect(() => {
    if (values.images && Array.isArray(values.images) && values.images.length > 0) {
      const firstImage = values.images[0] as ImageWithArtistId;
      if (firstImage.artistId && !artistIdforAssets) {
        setArtistIdforAssets(String(firstImage.artistId));
      }
      if (pitchImages.length === 0) {
        setPitchImages(values.images as ImageWithArtistId[]);
      }
    }
  }, [values.images, artistIdforAssets]);
  const artistSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleArtistInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFieldValue("artistName", value);

    if (artistSearchTimerRef.current) {
      clearTimeout(artistSearchTimerRef.current);
    }

    if (value.length > 0) {
      artistSearchTimerRef.current = setTimeout(async () => {
        try {
          const response = await artistsApi.getAll({ search: value });
          const results = response.data.data.artists || [];
          setFilteredArtists(results);
          setShowSuggestions(results.length > 0);
        } catch {
          setFilteredArtists([]);
          setShowSuggestions(false);
        }
      }, 300);
    } else {
      setFilteredArtists([]);
      setShowSuggestions(false);
    }
  };



  // Add this useEffect to close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (isDropdownOpen && !event.target.closest('.promo-photos-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleAddArtistClick = () => {
    if (setIsAddArtistModalOpen) {
      setIsAddArtistModalOpen(true);
    }
  };

  // Contributor handlers
  const handleAddContributor = () => {
    setContributorModalMode("add");
    setSelectedContributor(null);
    openModal("add-contributor");
  };

  const handleEditContributor = (contributor: Contributor) => {
    setContributorModalMode("edit");
    setSelectedContributor(contributor);
    openModal("add-contributor");
  };

  const handleSaveContributor = (contributorData: { name: string; roles: string; id?: string | number }) => {
    const currentContributors = (values.contributors || []) as Contributor[];

    const updatedContributors = contributorData.id
      ? currentContributors.map((c) =>
          c.id === contributorData.id ? { ...c, ...contributorData } : c
        )
      : [
          ...currentContributors,
          {
            ...contributorData,
            id: `contributor-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          } as Contributor,
        ];

    setFieldValue("contributors", updatedContributors);
    closeModal("add-contributor");
  };

  const handleDeleteContributor = (contributor: Contributor) => {
    setContributorToDelete(contributor);
    openModal("delete-contributor");
  };

  const confirmDeleteContributor = () => {
    if (contributorToDelete) {
      const currentContributors = (values.contributors || []) as Contributor[];
      const updatedContributors = currentContributors.filter((c) => c.id !== contributorToDelete.id);
      setFieldValue("contributors", updatedContributors);
      setContributorToDelete(null);
    }
  };
  return (
    <section className="p-4 bg-white border rounded-lg border-neutral-200 sm:p-6" data-tour="general-release-info-section">
      <h2 className="mb-6 text-xl font-semibold text-black">General Release Info</h2>

      {/* AI-Powered Tag Suggestions */}
      {hasAimsAccess && (
        <div className="mb-6 p-4 border border-neutral-200 rounded-lg bg-neutral-50">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-neutral-900">AI Suggestions</h3>
              <p className="text-xs text-neutral-500">Upload an audio file to auto-detect genre, mood, instruments, similar artists and playlists</p>
            </div>
            {(aimsStatus === "processing" || aimsStatus === "partial" || aimsStatus === "done" || aimsStatus === "error") && (
              <Button
                type="button"
                variant="link"
                size="inline"
                onClick={handleAimsReset}
                className="text-xs text-neutral-500 hover:text-neutral-700"
              >
                Reset
              </Button>
            )}
          </div>

          {/* Audio file selector + Use AIMS button */}
          {(aimsStatus === "idle" || aimsStatus === "uploading" || aimsStatus === "processing") && (
            <div className="flex items-center gap-3 flex-wrap">
              <input
                ref={audioInputRef}
                type="file"
                accept="audio/mpeg,audio/mp3"
                onChange={handleAudioFileSelect}
                // The browser's "Choose File" control can't be a <Button>; these file:
                // styles give it the secondary-button look so it matches its neighbours.
                className="block text-sm text-neutral-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-control file:border file:border-solid file:border-primary-300 file:bg-white file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-50 file:cursor-pointer file:transition-colors"
                disabled={aimsStatus === "uploading" || aimsStatus === "processing"}
              />
              {(() => {
                const isProcessing = aimsStatus === "uploading" || aimsStatus === "processing";
                const missingFile = !selectedAudioFile;
                const missingLyrics = !values.lyrics?.trim();
                const isDisabled = missingFile || missingLyrics || isProcessing;
                const tooltipParts: string[] = [];
                if (missingFile) tooltipParts.push("upload an audio file");
                if (missingLyrics) tooltipParts.push("fill in the lyrics");
                const tooltipContent = tooltipParts.length > 0
                  ? `Please ${tooltipParts.join(" and ")} to use AIMS`
                  : null;
                const btn = (
                  <Button
                    type="button"
                    onClick={handleAimsAnalyze}
                    loading={isProcessing}
                    disabled={isDisabled}
                  >
                    {isProcessing ? (
                      aimsStatus === "uploading" ? "Uploading..." : "Analyzing..."
                    ) : (
                      <>
                        <ArrowUpTrayIcon className="w-4 h-4" />
                        Use AIMS
                      </>
                    )}
                  </Button>
                );
                if (!tooltipContent) return btn;
                return (
                  <Tooltip.Provider delayDuration={200}>
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <span className="cursor-not-allowed">{btn}</span>
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content
                          className="z-50 max-w-sm px-3 py-2 text-sm text-white break-words bg-black border rounded-md shadow-lg"
                          side="top"
                          align="center"
                          sideOffset={5}
                        >
                          {tooltipContent}
                          <Tooltip.Arrow className="fill-black" />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  </Tooltip.Provider>
                );
              })()}
            </div>
          )}

          {aimsStatus === "error" && (
            <div className="mt-2">
              <p className="text-sm text-danger-600">{aimsError}</p>
            </div>
          )}

          {(aimsStatus === "processing" || aimsStatus === "partial" || aimsStatus === "done") && aimsResult && (
            <div className="space-y-3">
              {/* Genres / Moods / Instruments — only available after polling completes */}
              {aimsStatus === "done" && aimsResult.mapped && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div>
                    <p className="text-xs font-medium text-neutral-500 mb-1">Suggested Genres</p>
                    <div className="flex flex-wrap gap-1">
                      {aimsResult.mapped.genres.length > 0 ? (
                        aimsResult.mapped.genres.map((genre) => (
                          <span key={genre} className="inline-block px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-800 rounded-full">
                            {genre}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-neutral-400">No genres detected</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-neutral-500 mb-1">Suggested Moods</p>
                    <div className="flex flex-wrap gap-1">
                      {aimsResult.mapped.moods.length > 0 ? (
                        aimsResult.mapped.moods.map((mood) => (
                          <span key={mood} className="inline-block px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-800 rounded-full">
                            {mood}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-neutral-400">No moods detected</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-neutral-500 mb-1">Suggested Instruments{selectedDSPs?.spotify ? " (Spotify)" : ""}</p>
                    <div className="flex flex-wrap gap-1">
                      {aimsResult.mapped.instruments.length > 0 ? (
                        aimsResult.mapped.instruments.map((instrument) => (
                          <span key={instrument} className="inline-block px-2 py-0.5 text-xs font-medium bg-success-100 text-success-800 rounded-full">
                            {instrument}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-neutral-400">No instruments detected</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* Suggested Similar Artists — dropdown multiselect, max 3 */}
                {aimsResult.suggestedArtists.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-neutral-500 mb-1">Suggested Similar Artists (Amazon)</p>
                    <MultiSelect
                      options={aimsResult.suggestedArtists}
                      value={selectedAimsArtists}
                      onChange={setSelectedAimsArtists}
                      placeholder="Select up to 3 artists"
                      maxSelections={3}
                    />
                  </div>
                )}

                {/* Suggested Playlists — dropdown multiselect, reference only */}
                {aimsResult.suggestedPlaylists.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-neutral-500 mb-1">Suggested Playlists (reference)</p>
                    <MultiSelect
                      options={aimsResult.suggestedPlaylists.map((p) => p.title)}
                      value={selectedAimsPlaylists}
                      onChange={setSelectedAimsPlaylists}
                      placeholder="Select up to 3 playlists"
                      maxSelections={3}
                    />
                  </div>
                )}
              </div>  

              {/* Analyzing indicator shown while polling is still in progress */}
              {aimsStatus === "processing" && (
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <div className="w-3 h-3 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
                  Analyzing tags...
                </div>
              )}

              {/* Soft warning when polling never returned results */}
              {aimsStatus === "partial" && (
                <div className="flex items-center gap-2 px-3 py-2 text-xs text-warning-800 bg-warning-50 border border-warning-200 rounded-md">
                  <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  AI suggestions could not be retrieved. Please try again in a few minutes.
                </div>
              )}

              {aimsStatus !== "processing" && 
                <Button type="button" size="sm" onClick={applyAimsSuggestions}>
                  Apply Suggestions
                </Button>
              }
            </div>
          )}
        </div>
      )}

      <div className="mb-6">
        <div className="space-y-2">
          <label
            htmlFor="lyrics"
            className="block text-sm font-medium text-neutral-700"
          >
            Lyrics
          </label>
          <textarea
            id="lyrics"
            name="lyrics"
            placeholder="Enter Lyrics"
            value={values.lyrics || ""}
            onChange={(e) => setFieldValue("lyrics", e.target.value)}
            className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.lyrics && touched.lyrics ? 'border-danger-500' : 'border-neutral-300'
              }`}
          />
          <ErrorMessage name="lyrics" component="div" className="mt-1 text-sm text-danger-600" />
        </div>
      </div>

      {/* Only show this row if coming from top "Create Pitch" button */}
      {!state?.fromDashboard && (
        <div className="grid grid-cols-1 gap-6 mb-6 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2">
            {/* Pinned to the label's line height so the Add button doesn't push
                this input below the neighbouring Release Title / Date inputs. */}
            <div className="flex items-center justify-between h-5">
              <label
                htmlFor="artist-name"
                className="block text-sm font-medium text-neutral-700"
              >

                Artist Name <RequiredAsterisk />

              </label>
              {(showAddArtistButton && setIsAddArtistModalOpen) && (
                <Button
                  type="button"
                  onClick={handleAddArtistClick}
                  variant="soft"
                  size="sm"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add New Artist
                </Button>
              )}
            </div>
            <ArtistTypeahead
              value={values.artistName || ""}
              onChange={handleArtistSelection}
              placeholder="Enter Artist Name"
              error={errors.artistName}
              touched={touched.artistName}
            />
            <ErrorMessage name="artistName" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="release-title"
              className="block text-sm font-medium text-neutral-700"
            >

              Release Title     <RequiredAsterisk />
            </label>
            <input
              type="text"
              id="release-title"
              name="releaseTitle"
              placeholder="Enter Release Title"
              value={values.releaseTitle || ""}
              onChange={(e) => setFieldValue("releaseTitle", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.releaseTitle && touched.releaseTitle ? 'border-danger-500' : 'border-neutral-300'
                }`}
            />
            <ErrorMessage name="releaseTitle" component="div" className="mt-1 text-sm text-danger-600" />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="release-date"
              className="block text-sm font-medium text-neutral-700"
            >
              Release Date <RequiredAsterisk />
            </label>
            <input
              type="date"
              id="release-date"
              name="releaseDate"
              value={values.releaseDate || ""}
              onChange={(e) => setFieldValue("releaseDate", e.target.value)}
              // min={new Date().toISOString().split('T')[0]}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.releaseDate && touched.releaseDate ? 'border-danger-500' : 'border-neutral-300'
                }`}
            />
            <ErrorMessage name="releaseDate" component="div" className="mt-1 text-sm text-danger-600" />
          </div>
        </div>
      )}

      {/* Rest of the component remains the same */}
      {/* Core Release Information */}
      <div className="grid grid-cols-1 gap-6 mb-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-2">
          <label
            htmlFor="configuration"
            className="block text-sm font-medium text-neutral-700"
          >
            Configuration <RequiredAsterisk />
          </label>
          <MultiSelect
            options={configOptions}
            value={values.configuration || []}
            onChange={(newValues) => {
              setFieldValue("configuration", newValues);
              // Clear focus track if "Single" is in selection
              if (newValues.includes("Single")) {
                setFieldValue("focusTrackName", "");
              }
            }}
            placeholder="Select Configuration"
            maxSelections={2}
          />
          <ErrorMessage name="configuration" component="div" className="mt-1 text-sm text-danger-600" />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="release-upc"
            className="flex items-center gap-1 text-sm font-medium text-neutral-700"
          >
            Release UPC  <RequiredAsterisk />
            <InfoTooltip content="Most DSPs use the same UPC. Add platform-specific UPCs below only if needed (e.g., waterfall releases, exclusive versions, or platform-specific variants)." />
          </label>
          <input
            type="text"
            id="release-upc"
            name="releaseUpc"
            placeholder="Enter Release UPC"
            value={values.releaseUpc || ""}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, "");
              setFieldValue("releaseUpc", value);
            }}
            className={`w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors placeholder-neutral-400 focus:border-primary-500 ${errors.releaseUpc && touched.releaseUpc ? 'border-danger-500' : 'border-neutral-300'
              }`}
          />
          <ErrorMessage name="releaseUpc" component="div" className="mt-1 text-sm text-danger-600" />
        </div>

        {(Array.isArray(values.configuration) && (values.configuration.includes("EP") || values.configuration.includes("Album"))) && (
          <div className="space-y-2">
            <label
              htmlFor="focus-track"
              className="block text-sm font-medium text-neutral-700"
            >
              Focus Track <RequiredAsterisk />
            </label>
            <input
              type="text"
              id="focus-track"
              name="focusTrackName"
              placeholder="Enter Focus Track Name"
              value={values.focusTrackName || ""}
              onChange={(e) => setFieldValue("focusTrackName", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.focusTrackName && touched.focusTrackName ? 'border-danger-500' : 'border-neutral-300'
                }`}
            />
            <ErrorMessage name="focusTrackName" component="div" className="mt-1 text-sm text-danger-600" />
          </div>
        )}

        <div className="space-y-2">
          <label
            htmlFor="track-isrc"
            className="block text-sm font-medium text-neutral-700"
          >
            Track ISRC  <RequiredAsterisk />
          </label>
          <input
            type="text"
            id="track-isrc"
            name="trackIsrc"
            placeholder="Enter Track ISRC"
            value={values.trackIsrc || ""}
            onChange={(e) => setFieldValue("trackIsrc", e.target.value)}
            maxLength={20}
            className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.trackIsrc && touched.trackIsrc ? 'border-danger-500' : 'border-neutral-300'
              }`}
          />
          <ErrorMessage name="trackIsrc" component="div" className="mt-1 text-sm text-danger-600" />
        </div>
      </div>

      {/* Platform-Specific UPCs */}
      <div className="mb-6">
        <PlatformSpecificUpcs selectedDSPs={selectedDSPs} />
      </div>

      {/* Featured Artists and Alt Titles */}
      <div className="grid grid-cols-1 gap-6 mb-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-2">
          <label
            htmlFor="featured-artists"
            className="block text-sm font-medium text-neutral-700"
          >
            Featured Artists
          </label>
          <input
            type="text"
            id="featured-artists"
            name="featuredArtists"
            placeholder="Enter Featured Artists"
            value={values.featuredArtists || ""}
            onChange={(e) => setFieldValue("featuredArtists", e.target.value)}
            className="w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="artist-uri"
            className="block text-sm font-medium text-neutral-700"
          >
            Featured Artist URI
          </label>
          <input
            type="text"
            id="artist-uri"
            name="featuredArtistUri"
            placeholder="Enter URI"
            value={values.featuredArtistUri || ""}
            onChange={(e) => setFieldValue("featuredArtistUri", e.target.value)}
            className="w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="alt-titles"
            className="block text-sm font-medium text-neutral-700"
          >
            Alt Titles
          </label>
          <input
            type="text"
            id="alt-titles"
            name="altTitles"
            placeholder="Ex. Acoustic, Live, Spotify Sessions"
            value={values.altTitles || ""}
            onChange={(e) => setFieldValue("altTitles", e.target.value)}
            className="w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Description Fields */}

      <div className="mb-6 ">
        <div className="space-y-2">
          <label
            htmlFor="short-description"
            className="block text-sm font-medium text-neutral-700"
          >
            Description <RequiredAsterisk />
           
          </label>
          <div className="relative">
            <textarea
              id="short-description"
              name="shortDescription"
              placeholder="Description..."
              value={values.shortDescription || ""}
              onChange={(e) => {
                const characterCount = e.target.value.length;
                if (characterCount <= 500) {
                  setFieldValue("shortDescription", e.target.value);
                }
              }}
              maxLength={500}
              rows={5}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.shortDescription && touched.shortDescription ? 'border-danger-500' : 'border-neutral-300'
                }`}
            />
            {/* Character Counter for Description */}
            <div className="absolute bottom-2 right-3">

              <CharacterCounterDisplay
                value={values.shortDescription || ""}
                maxLength={500}

              />
            </div>
          </div>
          <ErrorMessage name="shortDescription" component="div" className="mt-1 text-sm text-danger-600" />
          <p className="text-sm text-neutral-500 ">
            Provide a compelling overview that combines the song's essence with its commercial appeal.
            Include an artist quote about the song's creation or meaning, musical context (genre, sonic comparisons, themes),
            and any momentum indicators that demonstrate why this track deserves editorial attention.
          </p>
        </div>
      </div>


      <div className="mb-6">
        <div className="space-y-2">
          <label
            htmlFor="long-description"
            className="block text-sm font-medium text-neutral-700"
          >
            Marketing Drivers <RequiredAsterisk />

          </label>
          <div className="relative">
            <textarea
              id="long-description"
              name="longDescription"
              placeholder={"• Music Video premiering on Holler Country\n• 5k influencer campaign spend\n• Troubadour debut day of release\n• Launch party at Soho House West Hollywood\n• Partnered with Eastman Guitars to giveaway a signed guitar at launch party\n• Joining Taylor Swift on select tour dates"}
              value={values.longDescription || ""}
              onChange={(e) => {
                const characterCount = e.target.value.length;
                if (characterCount <= 500) {
                  setFieldValue("longDescription", e.target.value);
                }
              }}
              // The placeholder is six bullets; rows={5} cut the last one off
              // behind a scrollbar, so the example read as truncated before the
              // user had typed anything. pb-8 keeps the text clear of the
              // character counter pinned at the bottom-right.
              rows={7}
              maxLength={500}
              className={`w-full px-3 py-2 pb-8 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.longDescription && touched.longDescription ? 'border-danger-500' : 'border-neutral-300'
                }`}
            />
            {/* Character Counter for Marketing Drivers */}
            <div className="absolute bottom-2 right-3">
              <CharacterCounterDisplay
                value={values.longDescription || ""}
                maxLength={500}
              />
            </div>
          </div>
          <ErrorMessage name="longDescription" component="div" className="mt-1 text-sm text-danger-600" />
          <p className="mt-2 text-sm text-neutral-500">
            Describe the primary promotional activities and strategic initiatives actively
            supporting this release. Include concrete marketing efforts, partnerships, and momentum-building
            activities that demonstrate industry investment and audience engagement potential.
          </p>
        </div>
      </div>  

      <div className="mb-6">
        <div className="space-y-2">
      {/* DSP-Specific Marketing Info Component */}
      <DspMarketingNotes selectedDSPs={selectedDSPs} />
        </div>
      </div>



      <div className="mb-6">
        <div className="space-y-2">
          <label
            htmlFor="expanded-description"
            className="block text-sm font-medium text-neutral-700"
          >
            Expanded Description
          </label>
          <textarea
            id="expanded-description"
            name="expandedDescription"
            placeholder="Give editors the full picture. Expand on details that didn't fit above—tour schedules, confirmed press, artist backstory, production credits, sync placements, cultural timing, partnership details, or any context that helps partners understand the full scope and importance of this release."
            value={values.expandedDescription || ""}
            onChange={(e) => setFieldValue("expandedDescription", e.target.value)}
            rows={6}
            className="w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Multi-select Dropdowns */}
      <div className="grid grid-cols-1 gap-6 mb-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-2">
          <label
            htmlFor="song-description"
            className="block text-sm font-medium text-neutral-700"
          >
            Describe the Song <RequiredAsterisk />
          </label>
          <MultiSelect
            options={dspDropdownOptions.songDescription}
            value={values.songDescription || []}
            onChange={(newValues) => setFieldValue("songDescription", newValues)}
            placeholder="Select Description"
          />
          <ErrorMessage name="songDescription" component="div" className="mt-1 text-sm text-danger-600" />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="main-genre"
            className="block text-sm font-medium text-neutral-700"
          >
            Main Genre <RequiredAsterisk />
          </label>
          <MultiSelect
            options={dspDropdownOptions.mainGenre}
            value={values.mainGenre || []}
            onChange={changeMainGenres}
            placeholder="Select Genre"
            maxSelections={3}
          />
          <ErrorMessage name="mainGenre" component="div" className="mt-1 text-sm text-danger-600" />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="song-mood"
            className="block text-sm font-medium text-neutral-700"
          >
            Song Mood  <RequiredAsterisk />
          </label>
          <MultiSelect
            options={dspDropdownOptions.songMood}
            value={values.songMood || []}
            onChange={(newValues) => setFieldValue("songMood", exclusiveMoods(values.songMood || [], newValues))}
            placeholder="Select Mood"
            maxSelections={3}
          />
          <ErrorMessage name="songMood" component="div" className="mt-1 text-sm text-danger-600" />
        </div>
      </div>

      {/* Links and Media */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-2">
          <label
            htmlFor="language"
            className="block text-sm font-medium text-neutral-700"
          >
            Language <RequiredAsterisk />
          </label>
          <MultiSelect
            options={dspDropdownOptions.language}
            value={values.language || []}
            onChange={(newValues) => setFieldValue("language", newValues)}
            placeholder="Select Language"
          />
          <ErrorMessage name="language" component="div" className="mt-1 text-sm text-danger-600" />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="link-to-listen"
            className="flex items-center gap-1 text-sm font-medium text-neutral-700"
          >
            Link to Listen  <RequiredAsterisk />
            <InfoTooltip content="Provide a publicly accessible streaming link. Disco links are preferred, but any
            link that doesn't require login credentials will work" />
          </label>
          <input
            type="url"
            id="link-to-listen"
            name="linkToListen"
            placeholder="Enter Link"
            value={values.linkToListen || ""}
            onChange={(e) => setFieldValue("linkToListen", e.target.value)}
            className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 ${errors.linkToListen && touched.linkToListen ? 'border-danger-500' : 'border-neutral-300'
              }`}
          />
          <ErrorMessage name="linkToListen" component="div" className="mt-1 text-sm text-danger-600" />
        </div>

        <div className="relative space-y-2 promo-photos-dropdown">
          <label className="flex items-center gap-1 text-sm font-medium text-neutral-700">
            Promo Photos / Images <RequiredAsterisk />
            <InfoTooltip content="Share high-quality promotional images via a public Google Drive link for editorial teams to use in playlist features and social promotion, or upload images directly." />
          </label>

          {/*
            Dropdown Toggle. Neutral at rest, primary once engaged — hovered,
            open, or focused. The open state is set directly rather than left to
            `focus:`: it shares a specificity with `hover:` and Tailwind emits
            hover last, so hover would otherwise win over open.
          */}
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`flex items-center justify-between w-full px-3 py-2 text-sm bg-white border rounded-md focus:outline-none ${
              (errors.promoPhotos && touched.promoPhotos) || (errors.images && touched.images) || (errors.promoPhotosCredit && touched.promoPhotosCredit)
                ? 'border-danger-500'
                : isDropdownOpen
                  ? 'border-primary-500'
                  : 'border-neutral-300 hover:border-primary-500 focus:border-primary-500'
            }`}
          >
            <span className="text-neutral-700">Select option...</span>
            <ChevronDownIcon className={`w-5 h-5 text-neutral-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Error Messages - Show at parent level */}
          {(errors.promoPhotos && touched.promoPhotos) && (
            <div className="mt-1 text-sm text-danger-600">{errors.promoPhotos}</div>
          )}
          {(errors.images && touched.images) && (
            <div className="mt-1 text-sm text-danger-600">{errors.images}</div>
          )}
          {(errors.promoPhotosCredit && touched.promoPhotosCredit) && (
            <div className="mt-1 text-sm text-danger-600">{errors.promoPhotosCredit}</div>
          )}

          {/* Dropdown Content */}
          {isDropdownOpen && (
            <div className="absolute left-0 right-0 z-10 p-4 mt-1 bg-white border border-neutral-200 rounded-md shadow-lg top-full">
              {/* URL Input Section */}
              <div className="mb-4 space-y-2">
                <label
                  htmlFor="promo-photos"
                  className="block text-xs font-medium text-neutral-700"
                >
                  Link to Photos
                </label>
                <input
                  type="url"
                  id="promo-photos"
                  name="promoPhotos"
                  placeholder="Enter Link to Photos"
                  value={values.promoPhotos || ""}
                  onChange={(e) => setFieldValue("promoPhotos", e.target.value)}
                  className="w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
                />
              </div>

              {/* Photo Credit for Link to Photos */}
              <div className="mb-4 space-y-2">
                <label
                  htmlFor="promo-photos-credit"
                  className="block text-xs font-medium text-neutral-700"
                >
                  Photo Credit {selectedDSPs?.TIDAL && values.promoPhotos ? <span className="text-danger-600">*</span> : ''}
                </label>
                <input
                  type="text"
                  id="promo-photos-credit"
                  name="promoPhotosCredit"
                  placeholder="Enter Photo Credit"
                  maxLength={200}
                  value={values.promoPhotosCredit || ""}
                  onChange={(e) => setFieldValue("promoPhotosCredit", e.target.value)}
                  className={`w-full px-3 py-2 text-sm text-neutral-700 border rounded-md focus:outline-none focus:border-primary-500 ${
                    selectedDSPs?.TIDAL && values.promoPhotos && !values.promoPhotosCredit ? 'border-danger-300' : 'border-neutral-300'
                  }`}
                />
                <div className="text-xs text-right text-neutral-400">
                  {(values.promoPhotosCredit || "").length}/200
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center my-3">
                <div className="flex-1 border-t border-neutral-300"></div>


              </div>

              {/* Choose from Assets Section */}
              <div className="space-y-2">
                <label className="block text-xs font-medium text-neutral-700">
                  Choose from assets
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setModalDefaultTab(1); // Set to All Assets tab
                    setIsModalOpen(true);
                  }}
                  className="flex items-center w-full px-4 py-2 rounded-lg border bg-white border-neutral-300 text-neutral-700 transition-colors hover:border-primary-300 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
                  style={{ minHeight: "48px" }}
                >
                  <FolderIcon className="w-5 h-5 mr-3 text-primary-700" />
                  <span className="flex-1 text-sm font-medium text-left">
                    Choose from assets
                  </span>
                </button>
              </div>

              {/* Divider */}
              <div className="flex items-center my-3">
                <div className="flex-1 border-t border-neutral-300"></div>
              </div>

              {/* Upload Section */}
              <div className="space-y-2">
                <label className="block text-xs font-medium text-neutral-700">
                  Upload Images
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setModalDefaultTab(0); // Set to Upload New Files tab
                    setIsModalOpen(true);
                  }}
                  className="flex items-center w-full px-4 py-2 rounded-lg border bg-white border-neutral-300 text-neutral-700 transition-colors hover:border-primary-300 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
                  style={{ minHeight: "48px" }}
                >
                  <ArrowUpTrayIcon className="w-5 h-5 mr-3 text-primary-700" />
                  <span className="flex-1 text-sm font-medium text-left">
                    Upload Images
                  </span>
                </button>
              </div>
            </div>
          )}

          <UploadModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            artistId={artistIdforAssets}
            onUploadSuccess={handleUploadSuccess}
            onSelectionChange={syncSelectedAssets}
            maxTotalImages={5}
            showCheckboxes={true}
            initialSelectedImages={pitchImages.map(img => ({ url: img.url, imageTitle: img.imageTitle }))}
            defaultTab={modalDefaultTab}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mt-5">
        {pitchImages.map((img, idx) => (
          <div
            key={idx}
            className="relative flex flex-col bg-white border border-neutral-200 rounded-lg shadow-sm"
            style={{ width: '160px' }}
          >
            {/* Delete button */}
            <button
              type="button"
              onClick={() => handleRemoveImage(idx)}
              className="absolute z-10 flex items-center justify-center w-6 h-6 text-white transition-colors bg-danger-500 rounded-full shadow-lg -top-2 -right-2 hover:bg-danger-600"
            >
              <XMarkIcon className="w-3 h-3" />
            </button>

            {/* Image container */}
            <div className="relative w-full h-20 overflow-hidden bg-neutral-100 rounded-t-lg">
              <img
                src={img.url}
                alt={img.imageTitle}
                className="object-cover w-full h-full"
              />
            </div>

            {/* Title */}
            <div className="p-2">
              <span className="block text-xs font-medium text-neutral-700 truncate">
                {img.imageTitle}
              </span>
            </div>

            {/* Photo Credit */}
            <div className="px-2 pb-2">
              <label className="block mb-1 text-xs font-medium text-neutral-600">
                Photo Credit {selectedDSPs?.TIDAL ? <span className="text-danger-600">*</span> : ''}
              </label>
              <input
                type="text"
                placeholder="Credit"
                maxLength={200}
                value={img.photoCredit || ""}
                onChange={(e) => {
                  const updatedImages = [...(values.images || [])];
                  if (updatedImages[idx]) {
                    updatedImages[idx] = { ...updatedImages[idx], photoCredit: e.target.value };
                    setFieldValue("images", updatedImages);
                  }
                  const updatedPitchImages = [...pitchImages];
                  updatedPitchImages[idx] = { ...updatedPitchImages[idx], photoCredit: e.target.value };
                  setPitchImages(updatedPitchImages);
                }}
                className={`w-full px-2 py-1 text-xs border rounded focus:outline-none focus:border-primary-500 ${
                  selectedDSPs?.TIDAL && !img.photoCredit ? 'border-danger-300' : 'border-neutral-300'
                }`}
              />
              <span className="block mt-0.5 text-[10px] text-neutral-400 text-right">
                {(img.photoCredit || "").length}/200
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Contributors Section */}
      <div className="mt-6 space-y-2">
        <div className="flex items-center justify-between">
          <label
            htmlFor="contributors"
            className="block text-sm font-medium text-neutral-700"
          >
            Contributors
          </label>
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleAddContributor();
            }}
            variant="soft"
            size="sm"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Contributors
          </Button>
        </div>

        {/* Contributor List */}
        {values.contributors && values.contributors.length > 0 && (
          <div className="mt-4">
            <ContributorList
              contributors={values.contributors}
              onEdit={handleEditContributor}
              onDelete={handleDeleteContributor}
            />
          </div>
        )}
      </div>

      {/*
        Per-recipient notes are edited in the Email Pitch modal, next to the
        recipient list they belong to. This page used to carry a second editor
        for the same `emailModalData.personalizedMessages` state; the modal
        gained its own, and one field with two editors is one too many.
      */}

      {/* Add/Edit Contributor Modal */}
      <ModalRenderer
        type="ADD_CONTRIBUTOR"
        isOpen={isOpen("add-contributor")}
        onClose={() => closeModal("add-contributor")}
        onSave={handleSaveContributor}
        contributor={selectedContributor}
        mode={contributorModalMode}
      />

      {/* Delete Confirmation Modal */}
      <ModalRenderer
        type="CONFIRMATION"
        isOpen={isOpen("delete-contributor")}
        onClose={() => closeModal("delete-contributor")}
        onConfirm={confirmDeleteContributor}
        title="Delete Contributor"
        message={`Are you sure you want to delete "${contributorToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
      />


    </section>
  );
};

export default GeneralReleaseInfo;